import Promise from 'bluebird';
import { MmcDataSource } from '../index';
import { configs } from '../configs';
import { memoizeWithAllArgsHashed as memoize } from '../utils';

const entityFactory = {
  feed: memoize(storeId =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: '',
      endpoint: configs.endpoints.feeds(storeId),
      idAttribute: 'Feeds',
    })),
  feedUpload: memoize((storeId, feedId) =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: '',
      endpoint: configs.endpoints.feedUploads(storeId, feedId),
    })),
  uploadLink: memoize((storeId, feedId, filename) =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: '',
      endpoint: configs.endpoints.uploadLink(storeId, feedId, filename),
    })),
  uploadFeed: memoize((storeId, feedId) =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: '',
      endpoint: configs.endpoints.uploadFeed(storeId, feedId),
    })),
  convertToImportFeed: memoize((storeId, feedId) =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: '',
      endpoint: configs.endpoints.convertToImportFeed(storeId, feedId),
    })),
  deleteFeeds: memoize(storeId =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: '',
      endpoint: configs.endpoints.deleteFeeds(storeId),
    })),
  downloadLink: memoize((storeId, feedId, feedUploadTrackerId, reportType) =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: '',
      endpoint: configs.endpoints.downloadlink(storeId, feedId, feedUploadTrackerId, reportType),
    })),
  getFeedLabelsByFeedId: memoize((storeId, feedId) =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: '',
      endpoint: configs.endpoints.getFeedLabelsByFeedId(storeId, feedId),
    })),
  getFeedLabels: memoize(storeId =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: '',
      endpoint: configs.endpoints.getFeedLabels(storeId),
    })),
};

export const feedQueries = {
  gridDataSource(storeId) {
    const entity = entityFactory.feed(storeId);

    return {
      type: 'jsdata',
      entity,
      primaryKey: 'Id',
      query: {},
    };
  },
  getFeedDetails(storeId, feedId) {
    const entity = entityFactory.feed(storeId);

    return entity.find(feedId);
  },
  getFeedUploads(storeId, feedId) {
    const entity = entityFactory.feedUpload(storeId, feedId);

    return entity.find();
  },
  getFeedDetailsWithUploads(storeId, feedId) {
    return Promise.all([
      this.getFeedDetails(storeId, feedId),
      this.getFeedUploads(storeId, feedId),
    ]).then(([details, uploads]) => {
      const merged =
      {
        ...details,
        AutoTagDetecting: uploads.AutoTagDetecting,
        Uploads: [
          ...uploads.FeedFileDetails,
        ],
      };

      return merged;
    });
  },
  createFeed(storeId, payload) {
    const entity = entityFactory.feed(storeId);

    return entity.create(payload);
  },
  updateFeed(storeId, feedId, payload) {
    const entity = entityFactory.feed(storeId);

    return entity.update(feedId, payload);
  },
  getUploadLink(storeId, feedId, filename) {
    const entity = entityFactory.uploadLink(storeId, feedId, filename);

    return entity.find();
  },
  uploadFeed(storeId, feedId, payload) {
    const entity = entityFactory.uploadFeed(storeId, feedId);

    return entity.create(payload);
  },
  convertToImportFeed(storeId, feedId, payload) {
    const entity = entityFactory.convertToImportFeed(storeId, feedId);

    return entity.create(payload);
  },
  deleteFeeds(storeId, feedIds) {
    const entity = entityFactory.deleteFeeds(storeId);

    return entity.create(feedIds);
  },
  getFeedDownloadUrl(storeId, feedId, feedUploadTrackerId, reportType) {
    const entity = entityFactory.downloadLink(storeId, feedId, feedUploadTrackerId, reportType);

    return entity.find();
  },
  getFeedLabelsByFeedId(storeId, feedId, page, pageSize, orderBy = null) {
    const entity = entityFactory.getFeedLabelsByFeedId(storeId, feedId);

    const params = {
      $top: pageSize,
      $skip: page < 1 ? 0 : pageSize * (page - 1),
    };

    if (orderBy) {
      params.$orderby = orderBy;
    }

    return entity.findAll({ query: params });
  },
  getFeedLabels(storeId, page, pageSize, searchText) {
    const entity = entityFactory.getFeedLabels(storeId);

    const params = {
      $top: pageSize,
      $skip: page < 1 ? 0 : pageSize * (page - 1),
    };

    if (searchText) {
      params.$filter = `contains(FeedLabel,'${searchText}')`;
    }

    return entity.findAll({ query: params });
  },
};
